import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Plus, Trash2, House, DollarSign, BarChart2 } from 'lucide-react';
import classNames from 'classnames';
import Select from '../../components/Select';
import Header from '../../components/Header';
import Tabs from '../../components/Tabs';
import TabsCut from '../../components/TabsCut';
import Actions from '../../components/Actions';
import Button from '../../components/Button';
import List from '../../components/List';
import Line from '../../components/Line';
import Text from '../../components/Text';
import Title from '../../components/Title';
import { COSTS_MAX_COUNT, SELECT_STYLES } from '../../utils/const';
import TabsList from '../../components/TabsList';
import TabsListItem from '../../components/TabsListItem';
import ConfirmationModal from '../../components/ConfirmationModal';
import NumberInput from '../../components/NumberInput';
import Textarea from '../../components/Textarea';
import MobileMenuHeader from '../../components/MobileMenuHeader';
import TabsListTitle from '../../components/TabsListTitle';
import Modal from '../../components/Modal';
import { COST_PRICE_OPTIONS } from '../../utils/factories/costPriceOptions';

const fields = ['name', 'amount', 'period'];

export default function FixedCost(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      common,
      tipCustomerSegment,
      caseStudies,
      costRevenue: {
        name,
        fixedCost: { header, title, text, list, tabs },
      },
    },
  } = props;
  const { collection } = useSelector((state) => state.cost);
  const {
    cost: { addCost, updateCost, removeCost },
  } = useDispatch();
  const [opened, setOpened] = useState(false);
  const [
    isPeriodConfirmationModalVisible,
    setIsPeriodConfirmationModalVisible,
  ] = useState(false);
  const [confirmationModalItemToUpdate, setConfirmationModalItemToUpdate] =
    useState(null);

  function selectValue(period) {
    return COST_PRICE_OPTIONS.find((opt) => opt.value === period) || null;
  }

  const getTableHeaderIconByIndex = (index) => {
    // eslint-disable-next-line default-case
    switch (index) {
      case 0:
        return <House className="w-6 h-auto" />;
      case 1:
        return <DollarSign className="w-6 h-auto" />;
      case 2:
        return <BarChart2 className="w-6 h-auto" />;
    }
  };

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon="icon-cost"
        locales={locales}
        componentIndex={componentIndex}
        className="bmc-page-header--violet"
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="FinanceCanvas" title={name} color="violet" />
        }
      />

      <Text align="center">{[text[0]]}</Text>

      <Title text={title[0]} align="left" />

      <List
        items={list}
        className="margin-top-40"
        icons={getTableHeaderIconByIndex}
      />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--violet">{title[1]}</span> {title[2]}
      </h2>

      <Text align="center">{[text[1]]}</Text>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-60 bmc-page-tabs--violet"
      >
        {[
          <div key="strategy">
            <div>
              {collection.map((item, index) => (
                <TabsList key={item.id} divider={index < COSTS_MAX_COUNT - 1}>
                  <TabsListTitle className="flex flex-row justify-between items-center">
                    {locales.cost.fixedCostTitle} {index + 1}
                    {collection.length > 1 && (
                      <ConfirmationModal
                        className="ml-3 hover:bg-muted-lighter/60 text-muted"
                        body={locales.common.deleteConfirmation}
                        confirmText="Delete"
                        buttonAriaLabel="Delete"
                        onConfirm={() => removeCost({ index, id: item.id })}
                      >
                        <Trash2 className="w-6 min-w-6 h-auto" />
                      </ConfirmationModal>
                    )}
                  </TabsListTitle>

                  <div className="flex flex-col gap-4">
                    {/* <ModalDeleteConfirmation
                      visible={isPeriodConfirmationModalVisible}
                      confirmationTitle="Are you sure you want to change the period for this cost item? This will reset the amount values to 0."
                      onClose={() => setIsPeriodConfirmationModalVisible(false)}
                      onDelete={() => {
                        setIsPeriodConfirmationModalVisible(false);
                      }}
                    /> */}

                    <Modal
                      closeButton={false}
                      isOpen={isPeriodConfirmationModalVisible}
                      onClose={() => setIsPeriodConfirmationModalVisible(false)}
                      title={locales.costRevenue.periodChangeConfirmation}
                    >
                      <div className="flex flex-row gap-3 items-end justify-end w-full">
                        <Button
                          onClick={() =>
                            setIsPeriodConfirmationModalVisible(false)
                          }
                        >
                          {common.cancel}
                        </Button>
                        <Button
                          variant="destructiveOutline"
                          onClick={() => {
                            updateCost(confirmationModalItemToUpdate);
                            updateCost({
                              ...confirmationModalItemToUpdate,
                              name: 'amount',
                              valueIndex: 0,
                              value: 0,
                              reset: true,
                            });
                            setIsPeriodConfirmationModalVisible(false);
                          }}
                        >
                          {common.change}
                        </Button>
                      </div>
                    </Modal>

                    {/* {isPeriodConfirmationModalVisible && (
                      <ConfirmationModal
                        className="ml-3 hover:bg-muted-lighter/60 text-muted"
                        body="Are you sure you want to change the period? The application will save your current amount values however it will use the first month amount."
                        confirmText="Change"
                        buttonAriaLabel="Change"

                        onConfirm={() => {
                          updateCost(confirmationModalItemToUpdate);
                          setIsPeriodConfirmationModalVisible(false);
                        }}
                      >
                        <Trash2 className="w-6 min-w-6 h-auto" />
                      </ConfirmationModal>
                    )} */}

                    <TabsListItem
                      divider={false}
                      className="mb-5"
                      icon={getTableHeaderIconByIndex(0)}
                      title={tabs[fields[0]]}
                      text={
                        // eslint-disable-next-line
                        <Textarea
                          delay={700}
                          value={item.name || ''}
                          onChange={(event) =>
                            updateCost({
                              index,
                              name: 'name',
                              value: event.target.value,
                              id: item.id,
                            })
                          }
                          placeholder={tabs.namePlaceholder}
                        />
                      }
                    />

                    <TabsListItem
                      divider={false}
                      fullWidth={item?.period === 'Manual' ?? false}
                      orientation={
                        item?.period !== 'Manual' ? 'horizontal' : 'vertical'
                      }
                      className="mb-5"
                      icon={getTableHeaderIconByIndex(1)}
                      title={tabs[fields[1]]}
                      text={
                        item?.period !== 'Manual' ? (
                          <NumberInput
                            doFloor
                            rows={1}
                            value={item?.amount?.[0] ?? 0}
                            onChange={(event) =>
                              updateCost({
                                index,
                                name: 'amount',
                                value: Number(event.target.value),
                                valueIndex: 0,
                                id: item.id,
                              })
                            }
                            placeholder={tabs.amountPlaceholder}
                          />
                        ) : (
                          // <div className="bmc-page-tabs-content-list-item-inputs-wrapper">
                          //   <div className="bmc-page-tabs-content-list-item-inputs">
                          //     {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                          //       (arrayItem, i) => (
                          //         <div
                          //           key={arrayItem}
                          //           className="bmc-page-tabs-content-list-item-inputs-input"
                          //         >
                          //           <div className="bmc-page-tabs-content-list-item-inputs-input-title">
                          //             {tabs.monthAbbreviation} {arrayItem}
                          //           </div>

                          //           <input
                          //             className="bmc-page-tabs-content-list-item-inputs-input-text"
                          //             onChange={(event) =>
                          //               updateCost({
                          //                 index,
                          //                 name: 'amount',
                          //                 valueIndex: i,
                          //                 value: Number(event.target.value),
                          //                 id: item.id,
                          //               })
                          //             }
                          //             value={item.amount?.[i] ?? 0}
                          //             placeholder={tabs.monthPlaceholder}
                          //             data-hj-allow
                          //             // test-key={`projected-sales-inputs-${productIndex}-input-${i}`}
                          //           />
                          //         </div>
                          //       ),
                          //     )}
                          //   </div>
                          // </div>

                          <div className="w-full overflow-x-auto overflow-y-hidden max-w-none h-full">
                            <table className="table-auto border-separate border-spacing-0 w-max min-w-full">
                              <thead className="w-full">
                                <tr>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                    (headItem, headItemIdx) => (
                                      <th
                                        key={headItemIdx}
                                        className="p-2 min-w-16 text-center text-sm whitespace-nowrap"
                                      >
                                        {tabs.monthAbbreviation} {headItem}
                                      </th>
                                    ),
                                  )}
                                </tr>
                              </thead>
                              <tbody className="w-full">
                                <tr>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                    (bodyItem, bodyItemIdx) => {
                                      const isFirst = bodyItemIdx === 0;
                                      const isLast = bodyItemIdx === 11;

                                      return (
                                        <td
                                          key={bodyItemIdx}
                                          className={`p-1 py-1.5 text-center border border-muted-darker bg-muted-lighter ${
                                            isFirst ? 'rounded-l-md' : ''
                                          } ${isLast ? 'rounded-r-md' : ''}`}
                                        >
                                          <div className="input-wrap relative">
                                            {/* This span acts as a "measuring stick" - it contains the same text as the input but is invisible. The browser calculates its natural width based on the content. */}
                                            <span
                                              className="width-machine p-2 text-center text-slate-900 invisible whitespace-nowrap"
                                              aria-hidden="true"
                                            >
                                              {item.amount[bodyItemIdx] ?? 0}
                                            </span>
                                            <input
                                              type="text"
                                              value={
                                                item.amount[bodyItemIdx] ?? 0
                                              }
                                              onChange={(e) => {
                                                const { value } = e.target;
                                                // Only allow positive numbers (including decimals)
                                                const positiveNumberRegex =
                                                  /^[0-9]*\.?[0-9]*$/;
                                                if (
                                                  value &&
                                                  !positiveNumberRegex.test(
                                                    value,
                                                  )
                                                ) {
                                                  e.target.value =
                                                    item.amount[bodyItemIdx] ??
                                                    0;
                                                  return;
                                                }

                                                // Defining a limit as parseInt can deal with a limited number
                                                const maxSafeNumberStringLength = 12;
                                                if (
                                                  value &&
                                                  value.length >
                                                    maxSafeNumberStringLength
                                                ) {
                                                  e.target.value =
                                                    item.amount[bodyItemIdx] ??
                                                    0;
                                                  return;
                                                }
                                                // Parse the value and update if valid
                                                const parsedValue =
                                                  parseFloat(value);
                                                if (
                                                  !Number.isNaN(parsedValue) &&
                                                  parsedValue >= 0
                                                ) {
                                                  updateCost({
                                                    index,
                                                    name: 'amount',
                                                    valueIndex: bodyItemIdx,
                                                    value: parsedValue,
                                                    id: item.id,
                                                  });
                                                } else if (value === '') {
                                                  // Allow empty input
                                                  updateCost({
                                                    index,
                                                    name: 'amount',
                                                    valueIndex: bodyItemIdx,
                                                    value: 0,
                                                    id: item.id,
                                                  });
                                                } else {
                                                  e.target.value =
                                                    item.amount[bodyItemIdx] ??
                                                    0;
                                                }
                                              }}
                                              onKeyUp={(e) => {
                                                // Update the width machine with current input value after the key is released
                                                const widthMachine =
                                                  e.target.parentElement.querySelector(
                                                    '.width-machine',
                                                  );
                                                if (widthMachine) {
                                                  widthMachine.textContent =
                                                    e.target.value || '0';
                                                }
                                              }}
                                              onFocus={(e) => {
                                                // Select all text when focused for easy editing
                                                e.target.select();
                                              }}
                                              className="input absolute w-full h-full p-2 text-center bg-transparent border-0 text-slate-900 focus:outline-none focus:ring-0 top-0 left-0"
                                              placeholder="0"
                                            />
                                          </div>
                                        </td>
                                      );
                                    },
                                  )}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )
                      }
                    />

                    <TabsListItem
                      divider={false}
                      className="mb-5"
                      icon={getTableHeaderIconByIndex(2)}
                      title={tabs[fields[2]]}
                      text={
                        // eslint-disable-next-line
                        <Select
                          styles={SELECT_STYLES}
                          className="text-black"
                          options={COST_PRICE_OPTIONS}
                          value={selectValue(item.period)}
                          placeholder={tabs.periodPlaceholder}
                          onChange={({ value }) => {
                            const updatedItem = {
                              index,
                              name: 'period',
                              value,
                              id: item.id,
                            };

                            // if we are changing from non manual to manual and viceversa we should reset the value to 0

                            const previousValue = item.period;
                            if (
                              (previousValue === 'Manual' &&
                                value !== 'Manual') ||
                              (previousValue !== 'Manual' && value === 'Manual')
                            ) {
                              setConfirmationModalItemToUpdate(updatedItem);
                              setIsPeriodConfirmationModalVisible(true);
                            } else {
                              updateCost(updatedItem);
                            }
                          }}
                        />
                      }
                    />
                  </div>
                </TabsList>
              ))}
            </div>

            {collection.length < COSTS_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions">
                <Button
                  variant="outline"
                  onClick={addCost}
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNewSomething}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  setOpened={setOpened}
                  opened={opened}
                  caseStudy={item}
                />

                <table className="w-full ">
                  <thead>
                    <tr>
                      {fields.map((field, i) => {
                        return (
                          <th
                            className={classNames('w-1/3', {
                              'px-4': i > 0 && i < fields.length - 1, // Padding left and right if not first or last
                            })}
                            key={field}
                          >
                            <div className="flex flex-row items-center mb-6">
                              <span className="p-2.5 bg-muted-lighter/10 rounded-md flex items-center justify-center mr-3">
                                {getTableHeaderIconByIndex(i)}
                              </span>
                              <span className="text-xl">{tabs[field]}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {item.costs.map((cost, i) => (
                      <tr key={i}>
                        {fields.map((field, fieldIndex) => (
                          <td
                            key={field}
                            className={classNames('text-start', {
                              'px-4':
                                fieldIndex > 0 &&
                                fieldIndex < fields.length - 1, // Padding left and right if not first or last
                            })}
                          >
                            <div className="mb-4">{cost[field]}</div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>
      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
